import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'galleryContainer',
    'modalImage',
    'modalContentImage',
    'prevArrow',
    'nextArrow',
    'slide',
    'dot',
  ];

  connect() {
    this.currentIndex = 0;
    this.showSlide(this.currentIndex);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.element.addEventListener('keydown', this.handleKeyDown);
    // Support both target and fallback selector for prev/next arrows
    const prevArrow = this.hasPrevArrowTarget
      ? this.prevArrowTarget
      : this.element.querySelector('.circle.left[data-prev], .circle.left');
    if (prevArrow) {
      prevArrow.addEventListener('click', this.prev.bind(this));
      prevArrow.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.prev();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.prev();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.next();
        }
      });
    }
    const nextArrow = this.hasNextArrowTarget
      ? this.nextArrowTarget
      : this.element.querySelector('.circle.right[data-next], .circle.right');
    if (nextArrow) {
      nextArrow.addEventListener('click', this.next.bind(this));
      nextArrow.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.next();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.prev();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.next();
        }
      });
    }
    this.dotTargets.forEach((dot, i) => {
      dot.addEventListener('click', () => this.goToSlide(i));
    });
    if (this.hasModalContentImageTarget) {
      this.modalContentImageTarget.onclick = this.closeModalImage.bind(this);
    }
  }

  disconnect() {
    this.element.removeEventListener('keydown', this.handleKeyDown);
  }

  showSlide(index) {
    if (index < 0 || index >= this.slideTargets.length) return;
    this.slideTargets.forEach((slide, i) => {
      const isActive = i === index;
      slide.classList.toggle('active', isActive);
      if (isActive) {
        slide.removeAttribute('inert');
      } else {
        slide.setAttribute('inert', '');
      }
      slide.removeAttribute('aria-hidden');
    });
    // Update dots: visual and ARIA
    this.dotTargets.forEach((dot, i) => {
      dot.classList.toggle('current', i === index);
      dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
      if (i === index) {
        dot.setAttribute('tabindex', '0');
      } else {
        dot.setAttribute('tabindex', '-1');
      }
    });
    this.currentIndex = index;
  }

  prev() {
    const newIndex = this.currentIndex > 0 ? this.currentIndex - 1 : this.slideTargets.length - 1;
    this.showSlide(newIndex);
  }

  next() {
    const newIndex = this.currentIndex < this.slideTargets.length - 1 ? this.currentIndex + 1 : 0;
    this.showSlide(newIndex);
  }

  goToSlide(index) {
    this.showSlide(index);
  }

  handleKeyDown(event) {
    if (event.key === 'ArrowLeft') {
      this.prev();
    } else if (event.key === 'ArrowRight') {
      this.next();
    } else if (
      event.key === 'Escape' &&
      this.hasModalImageTarget &&
      this.modalImageTarget.classList.contains('open')
    ) {
      this.closeModalImage();
    }
  }

  expandImage(e) {
    this.lastFocusedElement = document.activeElement;
    this.modalImageTarget.style.display = 'flex';
    this.modalImageTarget.classList.add('open');
    document.body.classList.add('modal-open');
    this.modalContentImageTarget.src = e.currentTarget.src;
    // Focus trap: focus modal content image
    if (this.hasModalContentImageTarget) {
      this.modalContentImageTarget.setAttribute('tabindex', '0');
      this.modalContentImageTarget.focus();
    }
  }

  expandImageIcon(e) {
    this.lastFocusedElement = document.activeElement;
    // Try to find the image to expand
    let img = null;
    // If the icon is inside a button or span, find the closest slide-image, then the image
    const slide = e.currentTarget.closest('.slide-image');
    if (slide) {
      img = slide.querySelector('.slider-image');
    }
    // Fallback: if the icon itself is an image
    if (!img && e.currentTarget.tagName === 'IMG') {
      img = e.currentTarget;
    }
    if (img && this.hasModalImageTarget && this.hasModalContentImageTarget) {
      this.modalImageTarget.style.display = 'flex';
      this.modalImageTarget.classList.add('open');
      document.body.classList.add('modal-open');
      this.modalContentImageTarget.src = img.src;
      this.modalContentImageTarget.setAttribute('tabindex', '0');
      this.modalContentImageTarget.focus();
    }
  }

  closeModalImage() {
    if (this.hasModalImageTarget) {
      this.modalImageTarget.style.display = 'none';
      this.modalImageTarget.classList.remove('open');
    }
    document.body.classList.remove('modal-open');
    // Remove tabindex from modal content image
    if (this.hasModalContentImageTarget) {
      this.modalContentImageTarget.removeAttribute('tabindex');
    }
    if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
      this.lastFocusedElement.focus();
      this.lastFocusedElement = null;
    }
  }
}
